/**
 * High Accuracy Location Service
 * Google Maps Web版レベルの高精度位置情報取得を実現
 * 
 * 主な機能:
 * - 複数回取得による精度向上
 * - カルマンフィルタリングによる平滑化
 * - 精度閾値による品質管理
 * - iOS「正確な位置情報」対応
 */

import { Location } from '../types/core';

export interface LocationResult {
    location: Location;
    accuracy: number; // メートル
    timestamp: number;
    source: 'gps' | 'network' | 'passive';
    confidence: 'high' | 'medium' | 'low';
}

export interface LocationOptions {
    targetAccuracy?: number; // 目標精度（メートル）
    maxAttempts?: number; // 最大試行回数
    timeout?: number; // タイムアウト（ミリ秒）
    enableSmoothing?: boolean; // 平滑化を有効にする
    progressCallback?: (progress: LocationProgress) => void;
}

export interface LocationProgress {
    attempt: number;
    maxAttempts: number;
    currentAccuracy?: number;
    targetAccuracy: number;
    bestAccuracy?: number;
    status: 'initializing' | 'acquiring' | 'improving' | 'completed' | 'failed';
    message: string;
}

/**
 * カルマンフィルタによる位置情報の平滑化
 */
class LocationKalmanFilter {
    private lat: number = 0;
    private lng: number = 0;
    private variance: number = 1000; // 初期分散
    private processNoise: number = 1; // プロセスノイズ

    update(measurement: { lat: number; lng: number; accuracy: number }): { lat: number; lng: number } {
        if (this.lat === 0 && this.lng === 0) {
            // 初回測定値
            this.lat = measurement.lat;
            this.lng = measurement.lng;
            this.variance = measurement.accuracy * measurement.accuracy;
            return { lat: this.lat, lng: this.lng };
        }

        // 予測ステップ
        const predictedVariance = this.variance + this.processNoise;

        // 更新ステップ
        const measurementVariance = measurement.accuracy * measurement.accuracy;
        const kalmanGain = predictedVariance / (predictedVariance + measurementVariance);

        // 状態更新
        this.lat = this.lat + kalmanGain * (measurement.lat - this.lat);
        this.lng = this.lng + kalmanGain * (measurement.lng - this.lng);
        this.variance = (1 - kalmanGain) * predictedVariance;

        return { lat: this.lat, lng: this.lng };
    }

    getEstimatedAccuracy(): number {
        return Math.sqrt(this.variance);
    }
}

/**
 * 高精度位置情報取得クラス
 */
export class HighAccuracyLocationService {
    private filter = new LocationKalmanFilter();
    private measurements: LocationResult[] = [];

    /**
     * 高精度位置情報を取得
     */
    async getHighAccuracyLocation(options: LocationOptions = {}): Promise<LocationResult> {
        const {
            targetAccuracy = 10, // 10メートル以下を目標
            maxAttempts = 5,
            timeout = 30000,
            enableSmoothing = true,
            progressCallback
        } = options;

        // 初期化
        this.filter = new LocationKalmanFilter();
        this.measurements = [];

        return new Promise((resolve, reject) => {
            let attempts = 0;
            let bestResult: LocationResult | null = null;
            let timeoutId: number;

            const reportProgress = (progress: LocationProgress) => {
                progressCallback?.(progress);
            };

            // タイムアウト設定
            timeoutId = window.setTimeout(() => {
                if (bestResult) {
                    resolve(bestResult);
                } else {
                    reject(new Error('位置情報の取得がタイムアウトしました'));
                }
            }, timeout);

            const tryGetPosition = () => {
                attempts++;

                reportProgress({
                    attempt: attempts,
                    maxAttempts,
                    targetAccuracy,
                    bestAccuracy: bestResult?.accuracy,
                    status: attempts === 1 ? 'initializing' : 'acquiring',
                    message: attempts === 1 ?
                        '高精度GPS取得を開始しています...' :
                        `精度向上中... (${attempts}/${maxAttempts}回目)`
                });

                const positionOptions: PositionOptions = {
                    enableHighAccuracy: true,
                    timeout: Math.min(15000, timeout / maxAttempts), // 試行回数に応じて調整
                    maximumAge: 0 // 常に新しい位置情報を取得
                };

                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        const coords = position.coords;

                        // 位置情報の品質評価
                        const confidence = this.evaluateConfidence(coords.accuracy);
                        const source = this.determineSource(coords);

                        const result: LocationResult = {
                            location: {
                                lat: coords.latitude,
                                lng: coords.longitude
                            },
                            accuracy: coords.accuracy,
                            timestamp: position.timestamp,
                            source,
                            confidence
                        };

                        this.measurements.push(result);

                        // カルマンフィルタによる平滑化
                        if (enableSmoothing && this.measurements.length > 1) {
                            const smoothed = this.filter.update({
                                lat: coords.latitude,
                                lng: coords.longitude,
                                accuracy: coords.accuracy
                            });

                            result.location = smoothed;
                            result.accuracy = Math.min(coords.accuracy, this.filter.getEstimatedAccuracy());
                        }

                        // ベスト結果の更新
                        if (!bestResult || result.accuracy < bestResult.accuracy) {
                            bestResult = result;
                        }

                        reportProgress({
                            attempt: attempts,
                            maxAttempts,
                            currentAccuracy: result.accuracy,
                            targetAccuracy,
                            bestAccuracy: bestResult.accuracy,
                            status: result.accuracy <= targetAccuracy ? 'completed' : 'improving',
                            message: result.accuracy <= targetAccuracy ?
                                `高精度取得完了！ (精度: ${Math.round(result.accuracy)}m)` :
                                `精度向上中... 現在: ${Math.round(result.accuracy)}m / 目標: ${targetAccuracy}m`
                        });

                        // 目標精度に達した場合、または最大試行回数に達した場合
                        if (result.accuracy <= targetAccuracy || attempts >= maxAttempts) {
                            window.clearTimeout(timeoutId);
                            resolve(bestResult);
                        } else {
                            // 次の試行まで少し待機（GPS安定化のため）
                            setTimeout(tryGetPosition, 1500);
                        }
                    },
                    (error) => {
                        if (attempts === 1) {
                            // 初回エラーの場合は即座に失敗
                            window.clearTimeout(timeoutId);
                            reject(error);
                        } else if (bestResult) {
                            // 既に結果がある場合はそれを返す
                            window.clearTimeout(timeoutId);
                            resolve(bestResult);
                        } else if (attempts >= maxAttempts) {
                            // 最大試行回数に達した場合
                            window.clearTimeout(timeoutId);
                            reject(error);
                        } else {
                            // 再試行
                            setTimeout(tryGetPosition, 1000);
                        }
                    },
                    positionOptions
                );
            };

            // 位置情報取得開始
            tryGetPosition();
        });
    }

    /**
     * 位置情報の信頼度を評価
     */
    private evaluateConfidence(accuracy: number): 'high' | 'medium' | 'low' {
        if (accuracy <= 10) return 'high';
        if (accuracy <= 50) return 'medium';
        return 'low';
    }

    /**
     * 位置情報のソースを推定
     */
    private determineSource(coords: GeolocationCoordinates): 'gps' | 'network' | 'passive' {
        // 精度とaltitudeの有無から推定
        if (coords.accuracy <= 20 && coords.altitude !== null) {
            return 'gps';
        } else if (coords.accuracy <= 100) {
            return 'network';
        } else {
            return 'passive';
        }
    }

    /**
     * 取得履歴をクリア
     */
    clearHistory(): void {
        this.measurements = [];
        this.filter = new LocationKalmanFilter();
    }

    /**
     * 測定履歴を取得
     */
    getMeasurements(): LocationResult[] {
        return [...this.measurements];
    }
}

/**
 * iOS「正確な位置情報」の設定状況をチェック
 */
export function checkiOSLocationAccuracy(): Promise<'precise' | 'reduced' | 'unknown'> {
    return new Promise((resolve) => {
        if (!('geolocation' in navigator)) {
            resolve('unknown');
            return;
        }

        // iOS 14+の「正確な位置情報」チェック
        const options: PositionOptions = {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 0
        };

        navigator.geolocation.getCurrentPosition(
            (position) => {
                // 精度から推定（iOS「おおよその位置情報」は通常1000m以上）
                if (position.coords.accuracy > 500) {
                    resolve('reduced');
                } else {
                    resolve('precise');
                }
            },
            () => {
                resolve('unknown');
            },
            options
        );
    });
}

/**
 * ブラウザ別の位置情報取得の最適化設定
 */
export function getBrowserOptimizedOptions(): PositionOptions {
    const userAgent = navigator.userAgent.toLowerCase();

    if (userAgent.includes('safari') && !userAgent.includes('chrome')) {
        // Safari
        return {
            enableHighAccuracy: true,
            timeout: 20000,
            maximumAge: 0
        };
    } else if (userAgent.includes('chrome')) {
        // Chrome
        return {
            enableHighAccuracy: true,
            timeout: 15000,
            maximumAge: 5000
        };
    } else if (userAgent.includes('firefox')) {
        // Firefox
        return {
            enableHighAccuracy: true,
            timeout: 25000,
            maximumAge: 0
        };
    } else {
        // その他
        return {
            enableHighAccuracy: true,
            timeout: 20000,
            maximumAge: 10000
        };
    }
}

// シングルトンインスタンス
export const highAccuracyLocationService = new HighAccuracyLocationService();

export default {
    HighAccuracyLocationService,
    highAccuracyLocationService,
    checkiOSLocationAccuracy,
    getBrowserOptimizedOptions
};